import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {injectIntl, intlShape, defineMessages} from 'react-intl';
import VM from 'scratch-vm';

import spriteLibraryContent from '../lib/libraries/sprites.json';
import customSprites from '../lib/libraries/custom-sprites';
import randomizeSpritePosition from '../lib/randomize-sprite-position';
import spriteTags from '../lib/libraries/sprite-tags';
import {sanitizeSvg} from 'scratch-svg-renderer';
import {fetchBrickLabGlobalImages} from '../reducers/bricklab-global-images';
import {getImageDisplayUrl} from '../lib/bricklab-image-utils';

import LibraryComponent from '../components/library/library.jsx';

// Merge custom sprites with default sprite library
const allSprites = [...spriteLibraryContent, ...customSprites];

const messages = defineMessages({
    libraryTitle: {
        defaultMessage: 'Choose a Sprite',
        description: 'Heading for the sprite library',
        id: 'gui.spriteLibrary.chooseASprite'
    }
});

class SpriteLibrary extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleItemSelect',
            'loadCustomSpriteAssets'
        ]);
    }

    componentDidMount () {
        // Refetch every time the library opens (component remounts when modal closes).
        // Previously we only fetched when categories.length === 0, so new admin categories
        // never appeared until a full page reload.
        const {loading} = this.props.bricklabGlobalImages;
        if (!loading) {
            const thunkAction = fetchBrickLabGlobalImages(true);
            thunkAction(this.props.dispatch, () => this.props.bricklabGlobalImages);
        }
    }
    handleItemSelect (item) {
        // Randomize position of library sprite
        randomizeSpritePosition(item);
        
        // Check if this is a custom sprite with rawURL assets that need to be loaded
        const hasRawURL = item.costumes && item.costumes.some(costume => costume.rawURL);
        
        if (hasRawURL) {
            // Load custom sprite assets into storage
            this.loadCustomSpriteAssets(item).then(updatedItem => {
                this.props.vm.addSprite(JSON.stringify(updatedItem)).then(() => {
                    this.props.onActivateBlocksTab();
                });
            })
                .catch(error => {
                    console.error('Error loading custom sprite assets:', error);
                    // Fallback: try to add sprite anyway
                    this.props.vm.addSprite(JSON.stringify(item)).then(() => {
                        this.props.onActivateBlocksTab();
                    });
                });
        } else {
            // Standard sprite from library
            this.props.vm.addSprite(JSON.stringify(item)).then(() => {
                this.props.onActivateBlocksTab();
            });
        }
    }
    
    loadCustomSpriteAssets (sprite) {
        // Use VM's runtime storage to ensure assets are properly attached
        const storageInstance = this.props.vm.runtime.storage;
        if (!storageInstance) {
            throw new Error('VM storage not available');
        }
        
        const AssetType = storageInstance.AssetType;
        const DataFormat = storageInstance.DataFormat;
        
        // Process all costumes that have rawURL
        const costumePromises = sprite.costumes.map(costume => {
            if (!costume.rawURL) {
                // Return costume as-is if no rawURL
                return Promise.resolve(costume);
            }
            
            // Fetch the image through SEEDS API proxy (avoids CORS issues)
            return fetch(costume.rawURL, {
                method: 'GET',
                credentials: 'include', // Include credentials for SEEDS API
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`Failed to fetch sprite asset: ${response.statusText}`);
                    }
                    
                    // Determine format from URL or response
                    const contentType = response.headers.get('content-type') || '';
                    const urlPath = new URL(costume.rawURL).pathname.toLowerCase();
                    let dataFormat = 'png'; // default
                    let assetType = AssetType.ImageBitmap;
                    
                    if (contentType.includes('svg') || urlPath.endsWith('.svg')) {
                        dataFormat = 'svg';
                        assetType = AssetType.ImageVector;
                    } else if (contentType.includes('jpeg') || contentType.includes('jpg') ||
                        urlPath.endsWith('.jpg') || urlPath.endsWith('.jpeg')) {
                        dataFormat = 'jpg';
                        assetType = AssetType.ImageBitmap;
                    } else if (contentType.includes('png') || urlPath.endsWith('.png')) {
                        dataFormat = 'png';
                        assetType = AssetType.ImageBitmap;
                    } else if (contentType.includes('gif') || urlPath.endsWith('.gif')) {
                        dataFormat = 'gif';
                        assetType = AssetType.ImageBitmap;
                    }
                    
                    // Get image data
                    if (dataFormat === 'svg') {
                        return response.text().then(svgText => {
                            // Convert SVG text to Uint8Array for sanitization
                            const svgBytes = new TextEncoder().encode(svgText);
                            
                            // Sanitize SVG (this expects Uint8Array and returns Uint8Array)
                            const sanitizedSvg = sanitizeSvg.sanitizeByteStream(svgBytes);
                            
                            // Create asset in VM's storage
                            const asset = storageInstance.createAsset(
                                assetType,
                                DataFormat.SVG,
                                new Uint8Array(sanitizedSvg),
                                null,
                                true // generate md5
                            );
                            
                            // Return updated costume with asset attached
                            const {rawURL: _rawURL, ...costumeWithoutRawURL} = costume;
                            return {
                                ...costumeWithoutRawURL,
                                asset: asset,
                                assetId: asset.assetId,
                                md5ext: `${asset.assetId}.svg`,
                                dataFormat: 'svg'
                            };
                        });
                    }
                    // For bitmap images (PNG, JPG, GIF)
                    return response.arrayBuffer().then(arrayBuffer => {
                        const imageData = new Uint8Array(arrayBuffer);
                        
                        // Create asset in VM's storage
                        const asset = storageInstance.createAsset(
                            assetType,
                            dataFormat,
                            imageData,
                            null,
                            true // generate md5
                        );
                        
                        // Return updated costume with asset attached
                        const {rawURL: _rawURL, ...costumeWithoutRawURL} = costume;
                        return {
                            ...costumeWithoutRawURL,
                            asset: asset,
                            assetId: asset.assetId,
                            md5ext: `${asset.assetId}.${dataFormat}`,
                            dataFormat: dataFormat
                        };
                    });
                });
        });
        
        return Promise.all(costumePromises).then(updatedCostumes => ({
            ...sprite,
            costumes: updatedCostumes
        }));
    }
    /**
     * Get dynamic tags from global image categories
     * @returns {Array} Array of tag objects for category tabs
     */
    getDynamicTags () {
        const {categories} = this.props.bricklabGlobalImages;
        // Convert category names to lowercase tags (matching Scratch's tag format)
        return categories.map(category => ({
            tag: category.name.toLowerCase(),
            intlLabel: {
                id: `gui.libraryTags.${category.name.toLowerCase()}`,
                defaultMessage: category.name
            }
        }));
    }

    /**
     * Convert global images to sprite format and combine with existing sprites
     * Each image becomes a sprite with one costume
     * @returns {Array} Combined array of sprites
     */
    getCombinedSpriteData () {
        const {categories} = this.props.bricklabGlobalImages;

        // Transform global images into sprite format
        // Each image becomes a sprite with one costume
        const globalImageSprites = categories.flatMap(category =>
            category.images.map(image => {
                const fullState = {
                    scratchGui: {bricklabGlobalImages: this.props.bricklabGlobalImages}
                };
                const imageUrl = getImageDisplayUrl(image, fullState);
                // Preserve the S3 key for later use
                // (either from s3Key or imageUrl if it's not a URL)
                const s3Key = image.s3Key ||
                    (image.imageUrl && !image.imageUrl.startsWith('http') ?
                        image.imageUrl :
                        null);

                // Create a sprite object with this image as its costume
                return {
                    name: image.name,
                    objName: image.name, // Required for SB2 format validation
                    md5ext: image.id, // Use image ID as unique identifier
                    rawURL: imageUrl, // Add rawURL at sprite level for LibraryComponent to display thumbnail
                    category: category.name.toLowerCase(), // Category for grouping in library (required for library.jsx grouping)
                    tags: [category.name.toLowerCase()], // Add as tag for filtering
                    isStage: false, // Required for SB3 format validation
                    variables: {}, // Required for SB3 format
                    blocks: {}, // Required for SB3 format
                    costumes: [{
                        name: image.name,
                        rawURL: imageUrl, // Use rawURL so loadCustomSpriteAssets will handle it
                        s3Key: s3Key, // Preserve S3 key
                        bitmapResolution: 1
                        // DO NOT set rotationCenterX/Y - leave undefined so renderer calculates center
                    }],
                    sounds: [],
                    volume: 100,
                    layerOrder: 1, // Required for SB3 format validation (must be >= 1)
                    visible: true,
                    x: 0,
                    y: 0,
                    size: 100,
                    direction: 90,
                    draggable: false,
                    rotationStyle: 'all around',
                    isGlobalImage: true // Flag to identify global images
                };
            })
        );

        // Combine static sprites with global image sprites (mixed together)
        return [...allSprites, ...globalImageSprites];
    }

    render () {
        const combinedData = this.getCombinedSpriteData();
        const dynamicTags = this.getDynamicTags();
        const existingTagKeys = new Set(spriteTags.map(t => t.tag));
        const newDynamicTags = dynamicTags.filter(t => !existingTagKeys.has(t.tag));
        const allTags = [...spriteTags, ...newDynamicTags];

        return (
            <LibraryComponent
                data={combinedData}
                id="spriteLibrary"
                tags={allTags}
                categoriesLoading={this.props.bricklabGlobalImages.loading}
                title={this.props.intl.formatMessage(messages.libraryTitle)}
                onItemSelected={this.handleItemSelect}
                onRequestClose={this.props.onRequestClose}
            />
        );
    }
}

SpriteLibrary.propTypes = {
    intl: intlShape.isRequired,
    onActivateBlocksTab: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func,
    vm: PropTypes.instanceOf(VM).isRequired,
    dispatch: PropTypes.func.isRequired,
    bricklabGlobalImages: PropTypes.shape({
        categories: PropTypes.array,
        loading: PropTypes.bool,
        error: PropTypes.string,
        lastFetched: PropTypes.number,
        imageUrlMap: PropTypes.object
    })
};

const mapStateToProps = state => ({
    bricklabGlobalImages: state.scratchGui.bricklabGlobalImages
});

export default connect(mapStateToProps)(injectIntl(SpriteLibrary));
